import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/format';
import { Plus, X, Pencil, Trash2, Bell, BellDot } from 'lucide-react';

const TYPE_COLOR = {
  'General':       'bg-slate-100 text-slate-600',
  'Policy Update': 'bg-violet-100 text-violet-700',
  'Holiday':       'bg-green-100 text-green-700',
  'Event':         'bg-blue-100 text-blue-700',
  'Safety Alert':  'bg-red-100 text-red-700',
  'Circular':      'bg-indigo-100 text-indigo-700',
  'Other':         'bg-slate-100 text-slate-500',
};

const blank = () => ({
  title: '', notice_type: 'General', body: '', important: false, expiry_date: '',
});

export default function AdminNotices() {
  const [items, setItems]   = useState([]);
  const [lookup, setLookup] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank());
  const [saving, setSaving]     = useState(false);
  const [selected, setSelected] = useState(null); // for reading a notice

  const load = () => {
    apiGet('/admin/notices?active_only=false').then(r => setItems(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { apiGet('/admin/lookup').then(setLookup).catch(() => {}); }, []);

  const openNew  = ()  => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ title: r.title, notice_type: r.notice_type, body: r.body, important: r.important, expiry_date: r.expiry_date || '' });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error('Title and Body are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiPut(`/admin/notices/${editing.id}`, form);
        toast.success('Notice updated');
      } else {
        await apiPost('/admin/notices', form);
        toast.success('Notice published');
      }
      setShowForm(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    try { await apiDel(`/admin/notices/${id}`); toast.success('Notice deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const types = lookup.notice_types || [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="admin-notices" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Administration</div>
          <h1 className="font-display text-2xl font-extrabold">Notices & Circulars</h1>
          <p className="text-sm text-slate-500 mt-0.5">Publish company-wide announcements, policies and alerts.</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="notice-add-btn">
          <Plus size={15} /> Publish Notice
        </button>
      </div>

      {/* Notice cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && (
          <div className="col-span-3 text-center text-sm text-slate-400 py-12">No notices published yet</div>
        )}
        {items.map(n => {
          const expired = n.expiry_date && n.expiry_date < today;
          return (
            <div key={n.id} data-testid={`notice-card-${n.id}`}
              className={`bg-white border rounded-sm p-4 flex flex-col gap-2 cursor-pointer hover:shadow-sm transition-shadow ${expired ? 'opacity-60' : ''} ${n.important ? 'border-amber-400 shadow-amber-100/50 shadow' : 'border-slate-200'}`}
              onClick={() => setSelected(n)}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {n.important ? <BellDot size={14} className="text-amber-500 shrink-0" /> : <Bell size={14} className="text-slate-400 shrink-0" />}
                  <span className={`tag text-[10px] ${TYPE_COLOR[n.notice_type] || 'bg-slate-100 text-slate-600'}`}>{n.notice_type}</span>
                </div>
                {expired && <span className="text-[9px] font-bold text-slate-400 uppercase">Expired</span>}
              </div>
              <h3 className="font-display font-bold text-sm text-[#0F172A]">{n.title}</h3>
              <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>
              <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
                <span className="text-[10px] text-slate-400">{n.published_by} · {formatDateTime(n.created_at)}</span>
                <div className="flex gap-2">
                  <button onClick={e => { e.stopPropagation(); openEdit(n); }} className="text-slate-400 hover:text-[#3b4872]" data-testid={`notice-edit-${n.id}`}><Pencil size={13} /></button>
                  <button onClick={e => { e.stopPropagation(); remove(n.id); }} className="text-red-400 hover:text-red-600" data-testid={`notice-del-${n.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Notice detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {selected.important && <BellDot size={16} className="text-amber-500" />}
                <span className={`tag text-[10px] ${TYPE_COLOR[selected.notice_type] || ''}`}>{selected.notice_type}</span>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <h2 className="font-display font-bold text-xl">{selected.title}</h2>
            <p className="text-sm text-slate-700 whitespace-pre-line">{selected.body}</p>
            <div className="text-xs text-slate-400 border-t pt-3">
              Published by <span className="font-semibold">{selected.published_by}</span> · {formatDateTime(selected.created_at)}
              {selected.expiry_date && <span> · Expires {formatDateTime(selected.expiry_date)}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Form Drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold text-sm uppercase tracking-wider">{editing ? 'Edit Notice' : 'Publish Notice'}</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </header>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="label">Title *</label>
                <input className="input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Office Closed on 26th Jan" data-testid="notice-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Type</label>
                  <select className="input" value={form.notice_type} onChange={e => setForm({...form, notice_type: e.target.value})}>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Expiry Date</label>
                  <input type="date" className="input" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Notice Content *</label>
                <textarea className="input h-40 resize-none" value={form.body} onChange={e => setForm({...form, body: e.target.value})} placeholder="Write the full notice text here…" data-testid="notice-body" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="important" checked={form.important} onChange={e => setForm({...form, important: e.target.checked})} className="w-4 h-4 accent-[#3b4872]" />
                <label htmlFor="important" className="text-sm cursor-pointer font-display font-semibold">Mark as Important (shown with alert highlight)</label>
              </div>
            </form>
            <footer className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="notice-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Publish')}</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
